"use client";

import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";
import { CheckCircle2, ChefHat, Flame, Martini } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

type TicketState = "queued" | "firing" | "ready" | "picked";
type CourseKey = "app" | "main" | "bar" | "dessert";

interface Ticket {
  id: string;
  table: string;
  item: string;
  mods?: string;
  state: TicketState;
  course: CourseKey;
  age: string;
}

const STATIONS: Array<{
  key: "app" | "main" | "bar";
  icon: React.ComponentType<{ className?: string }>;
  name: { en: string; es: string };
  tint: string;
}> = [
  { key: "app", icon: ChefHat, name: { en: "Cold / Apps", es: "Frío / Apps" }, tint: "bg-redy-orange/15 text-redy-orange" },
  { key: "main", icon: Flame, name: { en: "Grill / Hot", es: "Plancha / Hot" }, tint: "bg-redy-red/15 text-redy-red" },
  { key: "bar", icon: Martini, name: { en: "Bar", es: "Bar" }, tint: "bg-redy-blue/15 text-redy-blue" },
];

// Three frame-cycles to show coursing
const FRAMES: Array<{
  caption: { en: string; es: string };
  tickets: Ticket[];
}> = [
  {
    caption: {
      en: "Apps fire first. Bar pours in parallel.",
      es: "Apps disparan primero. Bar sirve en paralelo.",
    },
    tickets: [
      { id: "t1", table: "T12", item: "Caesar Salad", state: "firing", course: "app", age: "0:42" },
      { id: "t2", table: "T12", item: "Burrata Toast", mods: "GF", state: "firing", course: "app", age: "0:38" },
      { id: "t3", table: "T7", item: "Calamari", state: "queued", course: "app", age: "—" },
      { id: "b1", table: "T12", item: "Negroni", state: "firing", course: "bar", age: "0:28" },
      { id: "b2", table: "T12", item: "House Cab × 2", state: "ready", course: "bar", age: "0:08" },
      { id: "m1", table: "T12", item: "Ribeye", mods: "MR", state: "queued", course: "main", age: "—" },
      { id: "m2", table: "T12", item: "Linguine Vongole", state: "queued", course: "main", age: "—" },
    ],
  },
  {
    caption: {
      en: "Server marks apps picked → mains auto-fire.",
      es: "Mesero marca apps recogidas → mains se disparan.",
    },
    tickets: [
      { id: "t1", table: "T12", item: "Caesar Salad", state: "picked", course: "app", age: "✓" },
      { id: "t2", table: "T12", item: "Burrata Toast", mods: "GF", state: "picked", course: "app", age: "✓" },
      { id: "t3", table: "T7", item: "Calamari", state: "firing", course: "app", age: "0:48" },
      { id: "b1", table: "T12", item: "Negroni", state: "picked", course: "bar", age: "✓" },
      { id: "b2", table: "T12", item: "House Cab × 2", state: "picked", course: "bar", age: "✓" },
      { id: "m1", table: "T12", item: "Ribeye", mods: "MR", state: "firing", course: "main", age: "0:14" },
      { id: "m2", table: "T12", item: "Linguine Vongole", state: "firing", course: "main", age: "0:14" },
    ],
  },
  {
    caption: {
      en: "Mains ready. Plates leave hot, together.",
      es: "Mains listos. Salen calientes, juntos.",
    },
    tickets: [
      { id: "t3", table: "T7", item: "Calamari", state: "ready", course: "app", age: "0:04" },
      { id: "m1", table: "T12", item: "Ribeye", mods: "MR", state: "ready", course: "main", age: "0:02" },
      { id: "m2", table: "T12", item: "Linguine Vongole", state: "ready", course: "main", age: "0:00" },
    ],
  },
];

const STATE_STYLE: Record<
  TicketState,
  { card: string; pill: string; pillBg: string; ageText: string }
> = {
  queued: {
    card: "bg-white border-black/[0.06] opacity-50",
    pill: "Queued",
    pillBg: "bg-black/10 text-redy-charcoal/60",
    ageText: "text-redy-charcoal/40",
  },
  firing: {
    card: "bg-white border-redy-orange/40 ring-1 ring-redy-orange/20",
    pill: "Firing",
    pillBg: "bg-redy-orange text-white",
    ageText: "text-redy-orange",
  },
  ready: {
    card: "bg-redy-orange text-white border-redy-orange",
    pill: "Ready",
    pillBg: "bg-white text-redy-orange",
    ageText: "text-white",
  },
  picked: {
    card: "bg-redy-charcoal/[0.04] border-black/[0.04] opacity-60",
    pill: "Picked",
    pillBg: "bg-redy-charcoal/10 text-redy-charcoal/50",
    ageText: "text-redy-charcoal/30",
  },
};

export default function RestaurantsKdsCoursing() {
  const { t, lang } = useLang();
  const [frameIdx, setFrameIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setFrameIdx((i) => (i + 1) % FRAMES.length), 3600);
    return () => clearInterval(id);
  }, []);

  const frame = FRAMES[frameIdx];

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-[1140px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-4">
            {t("Multi-station KDS", "KDS multi-estación")}
          </div>
          <h2 className="text-[40px] md:text-[60px] font-black leading-[1.02] tracking-tight text-redy-charcoal mb-4">
            {t("Course by course.", "Curso por curso.")}
            <br />
            <span className="redy-text-gradient">
              {t("Station by station.", "Estación por estación.")}
            </span>
          </h2>
          <p className="text-lg text-redy-secondary max-w-[640px] mx-auto font-medium">
            {t(
              "Apps go to cold. Steaks go to grill. Drinks go to bar. The line fires the order in the right sequence, so the right plates leave together, hot.",
              "Las apps van a frío. Los cortes a la plancha. Las bebidas al bar. La línea dispara la orden en la secuencia correcta, para que los platos correctos salgan juntos, calientes."
            )}
          </p>
          <motion.div
            key={frameIdx}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-redy-charcoal bg-redy-warm-white border border-black/[0.05] px-4 py-2 rounded-full"
          >
            <span className="w-2 h-2 bg-redy-orange rounded-full animate-pulse" />
            {frame.caption[lang as "en" | "es"] ?? frame.caption.en}
          </motion.div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STATIONS.map((station) => {
            const Icon = station.icon;
            const tickets = frame.tickets.filter((tk) => tk.course === station.key);
            return (
              <motion.div
                key={station.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="bg-redy-warm-white border border-black/[0.05] rounded-3xl p-5"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-9 h-9 rounded-xl ${station.tint} flex items-center justify-center`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="font-black text-redy-charcoal">
                      {station.name[lang as "en" | "es"] ?? station.name.en}
                    </span>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/50">
                    {tickets.length} {t("active", "activas")}
                  </span>
                </div>

                <div className="space-y-2 min-h-[280px]">
                  {tickets.length === 0 && (
                    <div className="h-[260px] flex items-center justify-center text-xs text-redy-charcoal/30 font-medium italic">
                      {t("Clear", "Limpio")}
                    </div>
                  )}
                  {tickets.map((tk) => {
                    const sty = STATE_STYLE[tk.state];
                    return (
                      <motion.div
                        key={tk.id + frameIdx}
                        initial={{ opacity: 0, y: 6, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.35 }}
                        className={`border rounded-xl p-3 ${sty.card}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className="text-xs font-bold opacity-80">{tk.table}</span>
                          <span
                            className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${sty.pillBg}`}
                          >
                            {sty.pill}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="flex-1">
                            <div className="text-sm font-bold leading-tight">{tk.item}</div>
                            {tk.mods && (
                              <div className="text-[10px] opacity-70 mt-0.5">{tk.mods}</div>
                            )}
                          </div>
                          <div
                            className={`text-[10px] font-bold font-mono tabular-nums ${sty.ageText}`}
                          >
                            {tk.age}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 text-center"
        >
          <Link
            href="/features/kitchen-display-system"
            className="inline-flex items-center gap-2 text-sm font-bold text-redy-charcoal hover:text-redy-orange transition-colors"
          >
            <CheckCircle2 className="w-4 h-4 text-redy-orange" />
            {t(
              "How the multi-station KDS works →",
              "Cómo funciona el KDS multi-estación →"
            )}
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
